import React, { useCallback, useState } from 'react';
import { batch } from 'react-redux';
import { useForm } from 'react-final-form';
import { useEffectOnce } from 'js/hooks/useEffectOnce';
import { useComponentTranslations } from 'js/hooks/component';
import { TFormAddressLookupSwissInnerFields } from 'js/components/common/Form/types';

import * as Types from './types';
import * as hooks from './hooks';
import * as actions from '../../store/actions';
import { useDynamicReducerDispatch } from '../../store';
import { ZipCityLookup } from './components/ZipCityLookup';
import { StreetNameLookup } from './components/StreetNameLookup';
import { HouseNumberLookup } from './components/HouseNumberLookup';

import style from './AddressLookupSwissField.scss';

export const AddressLookupSwissField: React.FC<Types.IAddressLookupSwissFieldConfig> = ({
    field,
    fieldId,
    disabled,
    className,
    classNames,
    isFieldHidden,
    ChangeFormField,
    validate
}) => {
    const formAPI = useForm();
    const dispatch = useDynamicReducerDispatch();
    const translate = useComponentTranslations();
    const config = hooks.useAddressLookupSwissConfig();
    const [isManualInputEnabled, setManualInputEnabled] = useState(false);
    const [retrievedAddress, setRetrievedAddress] = useState<Types.IParsedAutoCompleteItem | null>(null);

    const hasInitialInnerFields = 'zip' in field.innerFields || 'city' in field.innerFields;

    const onRetrieve = useCallback((item: Types.IParsedAutoCompleteItem) => {
        setRetrievedAddress(item);

        const activeInnerFieldNames = Object.keys(field.innerFields) as (keyof TFormAddressLookupSwissInnerFields)[];

        formAPI.batch(() => {
            activeInnerFieldNames
                .forEach(name => {
                    item[name] && formAPI.change(field.innerFields[name].name, item[name]);
                });
        });

        batch(() => {
            activeInnerFieldNames
                .forEach(name => {
                    item[name] && dispatch(actions.removeHiddenField(field.innerFields[name].name));
                });
        });
    }, [dispatch, field, formAPI]);

    const hideInnerFields = useCallback(() => {
        batch(() => {
            dispatch(actions.removeHiddenField(field.name));
            Object.values(field.innerFields).forEach(innerField => dispatch(actions.addHiddenField(innerField.name)));
        });
    }, [dispatch, field]);

    const showInnerFields = useCallback(() => {
        batch(() => {
            Object.values(field.innerFields).forEach(innerField => dispatch(actions.removeHiddenField(innerField.name)));
        });
    }, [dispatch, field]);

    const handleActionManualClick = useCallback((event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        event.preventDefault();

        setRetrievedAddress(null);
        setManualInputEnabled(true);
        showInnerFields();
    }, [showInnerFields]);

    const handleActionResetClick = useCallback((event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        event.preventDefault();

        setRetrievedAddress(null);
        setManualInputEnabled(false);

        formAPI.batch(() => {
            (['zip', 'city', 'streetName', 'houseNumber'] as (keyof TFormAddressLookupSwissInnerFields)[])
                .forEach(name => {
                    field.innerFields[name] && formAPI.change(field.innerFields[name].name, '');
                });
        });

        hideInnerFields();
    }, [field, formAPI, hideInnerFields]);

    useEffectOnce(() => {
        if (config.isEnabled && hasInitialInnerFields) {
            setManualInputEnabled(false);
            hideInnerFields();

            return;
        }

        setManualInputEnabled(true);
        showInnerFields();
    }, [config, hasInitialInnerFields, hideInnerFields, showInnerFields]);

    return (
        <>
            <ZipCityLookup
                ChangeFormField={ChangeFormField}
                className={className}
                classNames={classNames}
                disabled={disabled}
                field={field}
                fieldId={`${fieldId}_zip-city`}
                validate={validate}
                isFieldHidden={isFieldHidden}
                onRetrieve={onRetrieve}
                retrievedAddress={retrievedAddress}
            />
            <StreetNameLookup
                ChangeFormField={ChangeFormField}
                className={className}
                classNames={classNames}
                disabled={disabled}
                field={field}
                fieldId={`${fieldId}_street-name`}
                isFieldHidden={isFieldHidden}
                onRetrieve={onRetrieve}
                retrievedAddress={retrievedAddress}
            />
            <HouseNumberLookup
                ChangeFormField={ChangeFormField}
                className={className}
                classNames={classNames}
                disabled={disabled}
                field={field}
                fieldId={`${fieldId}_house-number`}
                isFieldHidden={isFieldHidden}
                onRetrieve={onRetrieve}
                retrievedAddress={retrievedAddress}
            />
            {config.isEnabled && hasInitialInnerFields && !isFieldHidden && (
                <a
                    href="#"
                    className={style.toggleManualInput}
                    onClick={isManualInputEnabled ? handleActionResetClick : handleActionManualClick}
                >{isManualInputEnabled
                    ? translate('playtech.mobile.system.form.addressLookupSwiss.action.reset')
                    : translate('playtech.mobile.system.form.addressLookupSwiss.action.manually')
                }</a>
            )}
        </>
    );
};
